import { useEffect, useState } from 'react';

import { DocumentTypeWithVirtual, MessageV2DTO } from 'support/types';
import { SendConfirmationModal } from 'pages/Transactions/components/CreateDocument/components/SendConfirmationModal/SendConfirmationModal';

import { EphemeralPartnerConfirmationModal } from './EphemeralPartnerConfirmationModal/EphemeralPartnerConfirmationModal';
import { EphemeralPartnerAnnotation, findEphemeralPartnerAnnotation } from './helpers/findEphemeralPartnerAnnotation';
import { findUnmatchedArticleAnnotations } from './helpers/findUnmatchedArticleAnnotations';
import { useEphemeralPartnerDisplayData } from './helpers/useEphemeralPartnerDisplayData';
import { UnmatchedArticleConfirmationModal } from './UnmatchedArticleConfirmationModal/UnmatchedArticleConfirmationModal';

type SendConfirmationModalFlowProps = {
  open: boolean;
  message: MessageV2DTO;
  onSubmit: (annotationIds: Array<string>) => void;
  onCancel: () => void;
  showSummary?: boolean;
};

type PreSendStep = 'ephemeral' | 'unmatchedArticle';

export const SendConfirmationModalFlow = ({
  open,
  message,
  onSubmit,
  onCancel,
  showSummary = false,
}: SendConfirmationModalFlowProps) => {
  const ephemeralAnnotation = findEphemeralPartnerAnnotation(message);
  const unmatchedArticles = findUnmatchedArticleAnnotations(message);

  const steps: Array<PreSendStep> = [
    ...(ephemeralAnnotation ? (['ephemeral'] as const) : []),
    ...(unmatchedArticles.length > 0 ? (['unmatchedArticle'] as const) : []),
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [annotationIdsToResolve, setAnnotationIdsToResolve] = useState<Array<string>>([]);

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      setAnnotationIdsToResolve([]);
    }
  }, [open]);

  const reset = () => {
    setStepIndex(0);
    setAnnotationIdsToResolve([]);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const registerAndAdvance = (ids: Array<string>) => {
    setAnnotationIdsToResolve((previous) => [...previous, ...ids]);
    setStepIndex((index) => index + 1);
  };

  const handleFinalConfirm = () => {
    const annotationIds = annotationIdsToResolve;
    reset();
    onSubmit(annotationIds);
  };

  if (!open) {
    return null;
  }

  const currentStep = steps[stepIndex];

  if (currentStep === 'ephemeral' && ephemeralAnnotation) {
    return (
      <EphemeralPartnerConfirmationModalFromContext
        ephemeralAnnotation={ephemeralAnnotation}
        onConfirm={() => registerAndAdvance([ephemeralAnnotation.id])}
        onCancel={handleCancel}
      />
    );
  }

  if (currentStep === 'unmatchedArticle') {
    return (
      <UnmatchedArticleConfirmationModal
        open
        annotations={unmatchedArticles}
        onConfirm={(checkedIds) => registerAndAdvance(checkedIds)}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <SendConfirmationModal
      open
      documentType={message.type as DocumentTypeWithVirtual}
      receiverTradePartner={message.receiver_partner ?? undefined}
      showSummary={showSummary}
      onConfirm={handleFinalConfirm}
      onCancel={handleCancel}
    />
  );
};

type EphemeralPartnerConfirmationModalFromContextProps = {
  ephemeralAnnotation: EphemeralPartnerAnnotation;
  onConfirm: () => void;
  onCancel: () => void;
};

const EphemeralPartnerConfirmationModalFromContext = ({
  ephemeralAnnotation,
  onConfirm,
  onCancel,
}: EphemeralPartnerConfirmationModalFromContextProps) => {
  const { partnerName, partnerIdentifiers } = useEphemeralPartnerDisplayData(ephemeralAnnotation);

  return (
    <EphemeralPartnerConfirmationModal
      open
      partnerName={partnerName}
      partnerIdentifiers={partnerIdentifiers}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
